import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmPage from "./CrmPage";
import { crmFetch, formatMoney } from "./crmApi";
import "./Crm.css";

const BLANK = { name: "", description: "", items: [{ label: "", amount: "" }] };

/**
 * Price packages — the menu a studio quotes from. Editable here in full;
 * the quote builder loads them with one tap.
 */
function CrmPackages() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);
  const [editing, setEditing] = useState(null); // null closed, {} new, {...} existing
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/packages");
      setPackages(d.packages || []);
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ ...BLANK, items: [{ label: "", amount: "" }] });
    setError("");
    setEditing({});
  };

  const openEdit = (pkg) => {
    setForm({
      name: pkg.name,
      description: pkg.description || "",
      items: pkg.items.length
        ? pkg.items.map((i) => ({ label: i.label, amount: String(i.amount) }))
        : [{ label: "", amount: "" }],
    });
    setError("");
    setEditing(pkg);
  };

  const setItem = (idx, key, value) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
    }));

  const total = form.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  const save = async () => {
    const items = form.items
      .map((i) => ({ label: i.label.trim(), amount: Number(i.amount) || 0 }))
      .filter((i) => i.label);
    if (!form.name.trim()) {
      setError("Give the package a name.");
      return;
    }
    if (items.length === 0) {
      setError("Add at least one item with a name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = { name: form.name, description: form.description, items };
      if (editing.id) {
        await crmFetch(`/api/crm/packages/${editing.id}`, { method: "PUT", body });
      } else {
        await crmFetch("/api/crm/packages", { method: "POST", body });
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.name}"? Quotes already sent keep their items.`)) return;
    try {
      await crmFetch(`/api/crm/packages/${pkg.id}`, { method: "DELETE" });
      load();
    } catch (e) {
      window.alert(e.message);
    }
  };

  return (
    <CrmPage
      title="📦 Packages"
      subtitle="Your standard offerings — quote from these instead of retyping"
      loading={loading}
      planBlocked={planBlocked}
      actions={<button className="crm-primary-btn" onClick={openNew}>＋ New Package</button>}
    >
      {packages.length === 0 ? (
        <div className="crm-empty">
          <span className="crm-empty-icon">📦</span>
          No packages yet.
          <div className="crm-empty-hint">
            Build your usual offerings once — "Wedding Gold", "Half-day shoot" — then every
            quote starts one tap away.
          </div>
        </div>
      ) : (
        <div className="crm-pkg-grid">
          {packages.map((p) => {
            const sum = p.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
            return (
              <div key={p.id} className="crm-card crm-pkg-card">
                <div className="crm-pkg-card-head">
                  <div>
                    <h3>{p.name}</h3>
                    {p.description && <p className="crm-row-sub">{p.description}</p>}
                  </div>
                  <span className="crm-pkg-total">{formatMoney(sum) || "—"}</span>
                </div>
                <ul className="crm-pkg-items">
                  {p.items.map((i, idx) => (
                    <li key={idx}>
                      <span>{i.label}</span>
                      <span>{formatMoney(i.amount) || "—"}</span>
                    </li>
                  ))}
                </ul>
                <div className="crm-quote-card-actions">
                  <button className="crm-btn ghost" onClick={() => openEdit(p)}>✏️ Edit</button>
                  <button className="crm-btn danger" onClick={() => remove(p)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <div className="crm-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="crm-modal crm-quote-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing.id ? "Edit package" : "＋ New package"}</h3>
            {error && <div className="crm-form-error">{error}</div>}
            <div className="crm-field full">
              <label>Package name</label>
              <input
                value={form.name}
                autoFocus
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Wedding Gold"
              />
            </div>
            <div className="crm-field full" style={{ marginTop: 10 }}>
              <label>Short description (optional)</label>
              <input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Who it's for, what makes it different"
              />
            </div>

            <div className="crm-quote-items" style={{ marginTop: 14 }}>
              <div className="crm-quote-items-head">
                <span>Item</span><span>Amount</span><span />
              </div>
              {form.items.map((it, idx) => (
                <div className="crm-quote-item-row" key={idx}>
                  <input
                    value={it.label}
                    onChange={(e) => setItem(idx, "label", e.target.value)}
                    placeholder={idx === 0 ? "e.g. Full-day candid photography" : "Another item…"}
                  />
                  <input
                    type="number"
                    min="0"
                    value={it.amount}
                    onChange={(e) => setItem(idx, "amount", e.target.value)}
                    placeholder="0"
                  />
                  <button
                    type="button"
                    className="crm-task-del"
                    onClick={() => setForm((f) => ({
                      ...f,
                      items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
                    }))}
                  >✕</button>
                </div>
              ))}
              <button
                type="button"
                className="crm-link-btn"
                onClick={() => setForm((f) => ({ ...f, items: [...f.items, { label: "", amount: "" }] }))}
              >＋ Add item</button>
            </div>

            <div className="crm-quote-totals">
              <div className="crm-kv crm-quote-grand">
                <span className="k">Package total</span>
                <span className="v">{formatMoney(total) || "0"}</span>
              </div>
            </div>

            <div className="crm-modal-actions">
              <button className="crm-btn ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="crm-btn primary" disabled={saving} onClick={save}>
                {saving ? "Saving…" : editing.id ? "Save changes" : "Create package"}
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmPage>
  );
}

export default CrmPackages;
